import { Spin } from "@arco-design/web-react";
import { Bus, Footprints, Plane, Train } from "lucide-react";

import { formatSegmentRouteSummary, getSegmentRouteSummaryFacts, requiresLongBusReview, requiresRouteConfirmation } from "../service";
import type { ItinerarySegment } from "../types";

const transportIcons = {
  rail: Train,
  air: Plane,
  bus: Bus,
  walk: Footprints,
} as const;

/** 交通节点只展示方式与核心事实，统一空状态并保留完整读屏说明。 */
export function ItineraryRouteSummaryCard({
  segment,
  descriptionId,
  hideTransportMode = false,
  hideShortBusDetails = false,
  calculating = false,
}: {
  segment: ItinerarySegment;
  descriptionId: string;
  hideTransportMode?: boolean;
  hideShortBusDetails?: boolean;
  calculating?: boolean;
}) {
  const facts = getSegmentRouteSummaryFacts(segment, { hideShortBusDetails });
  const TransportIcon = transportIcons[segment.transportMode];
  const metadata = [facts.time, facts.duration, facts.distance, facts.backups].filter((item): item is string => Boolean(item));
  const routeCode = segment.transportMode === "rail" || segment.transportMode === "air"
    ? segment.sourcePayload?.selected?.routeCode?.trim() || segment.routeCode?.trim()
    : null;
  const status = calculating ? "正在计算交通" : requiresRouteConfirmation(segment) ? "待确认路线"
    : !hideTransportMode && requiresLongBusReview(segment) ? "超长大巴，待核对" : null;
  const description = [formatSegmentRouteSummary(segment, { hideTransportMode, hideShortBusDetails }), status].filter(Boolean).join(" · ");

  if (segment.transportMode === "walk") {
    return <>
      <span className="itinerary-route-walk" aria-hidden="true">
        <TransportIcon size={14} />
        {!hideTransportMode ? <strong>{facts.transportModeLabel}</strong> : null}
        {metadata.map((item) => <span key={item}>· {item}</span>)}
      </span>
      {status ? <b className={calculating ? "is-calculating" : undefined} aria-hidden="true">{calculating ? <Spin size={12} /> : null}{status}</b> : null}
      <span id={descriptionId} className="sr-only" role={calculating ? "status" : undefined}>{description}</span>
    </>;
  }

  return <>
    <span className={`itinerary-route-card is-${segment.transportMode}`} aria-hidden="true">
      <span className="itinerary-route-card-heading">
        <span className="itinerary-route-card-primary">
          <span className="itinerary-route-card-mode"><TransportIcon size={14} /></span>
          {!hideTransportMode ? <strong>{facts.transportModeLabel}</strong> : null}
          {routeCode ? <span className="itinerary-route-card-name">{routeCode}</span> : null}
        </span>
        {facts.travelClasses.length ? <span className="itinerary-route-card-classes">
          {facts.travelClasses.map((travelClass, index) => <span className="itinerary-route-card-class" key={`${index}-${travelClass}`}>{travelClass}</span>)}
        </span> : null}
      </span>
      {metadata.length ? <span className="itinerary-route-card-meta">
        {metadata.map((item) => <span key={item}>{item}</span>)}
      </span> : null}
    </span>
    {status ? <b className={calculating ? "is-calculating" : undefined} aria-hidden="true">{calculating ? <Spin size={12} /> : null}{status}</b> : null}
    <span id={descriptionId} className="sr-only" role={calculating ? "status" : undefined}>{description}</span>
  </>;
}
